import { Component, EventEmitter, Inject, OnInit, Output, InjectionToken } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { SessionManager, SessionManagerV1, sessionNgProvider } from '@onviobr/security-services';
import { lastValueFrom } from '@onviobr/helpers';


// Do NOT send authorization headers to these domains, it will break CORS.
const authorizationHeaderBlacklist = [
	// Product insight
	new RegExp('https://collector-product-insight.thomsonreuters.com/'),
	// Dynamsoft WebTwain (scanning driver)
	new RegExp('https://127.0.0.1:18619'),
	// Pendo data end-point
	new RegExp('https://data.pendo.io/'),
	/https:\/\/.*datadoghq.com/,
];

export const bluePendoToken = new InjectionToken('bluePendoToken');
export const bmDatadogToken = new InjectionToken('bmDatadog');
export const blueReleaseNotesToken = new InjectionToken('blueReleaseNotes');
export const blueStaffWidgetToken = new InjectionToken('blueStaffWidget');
export const sessionInterceptorToken = new InjectionToken('sessionInterceptor');
export const loggingInterceptorToken = new InjectionToken('sessionInterceptor');

@Component({
	selector: 'app-hybrid-cookie',
	template: '',
	providers: [
		sessionNgProvider,
		{ provide: bluePendoToken, useFactory: () => import('@onviobr/pendo-global') },
		{ provide: bmDatadogToken, useFactory: () => import('@onviobr/datadog') },
		{ provide: blueReleaseNotesToken, useFactory: () => import('@blue/release-notes') },
		{ provide: blueStaffWidgetToken, useFactory: () => import('@onviobr/staff-widgets') },
		{ provide: sessionInterceptorToken, useFactory: () => import('@onviobr/services/session/interceptor') },
		{ provide: loggingInterceptorToken, useFactory: () => import('@onviobr/services/logging/interceptor') },
	]
})
export class HybridCookieComponent implements OnInit {
	@Output() initialized: EventEmitter<void> = new EventEmitter();

	constructor(
		public cookieService: CookieService,
		@Inject(SessionManager) private readonly sessionManager: SessionManagerV1,
		@Inject(bluePendoToken) private readonly bluePendo: Promise<typeof import('@onviobr/pendo-global')>,
		@Inject(bmDatadogToken) private readonly bmDatadog: Promise<typeof import('@onviobr/datadog')>,
		@Inject(blueReleaseNotesToken) private readonly releaseNotes: Promise<typeof import('@blue/release-notes')>,
		@Inject(blueStaffWidgetToken) private readonly staffWidget: Promise<typeof import('@onviobr/staff-widgets')>,
		@Inject(sessionInterceptorToken)
		private readonly sessionInterceptor: Promise<typeof import('@onviobr/services/session/interceptor')>,
		@Inject(loggingInterceptorToken)
		private readonly loggingInterceptor: Promise<typeof import('@onviobr/services/logging/interceptor')>,
	) {
		this.setInterceptorToken = this.setInterceptorToken.bind(this);
	}

	public ngOnInit() {
		this.asyncInit().catch(err => console.error(err));
	}

	public setInterceptorToken(request: Request | string) {
		if (authorizationHeaderBlacklist.some(domain => {
			if (request instanceof Request) {
				return domain.test(request.url);
			} else {
				return domain.test(request);
			}
		})) {
			return null;
		}
		return this.cookieService.get('UDSLongToken') || window.sessionStorage.getItem('onvio-uds-token');
	};

	private async asyncInit(): Promise<void> {
		const { initialize: pendoInit } = await this.bluePendo;
		const { setDatadogUserContext } = await this.bmDatadog;
		const { registerReleaseNotes, openReleaseNotesModal } = await this.releaseNotes;
		const { register: registerStaffWidget } = await this.staffWidget;
		const { init: sessionInterceptor } = await this.sessionInterceptor;
		const { init: loggingInterceptor } = await this.loggingInterceptor;

		const session = await lastValueFrom(this.sessionManager.currentSession);
		const companyId = await lastValueFrom(this.sessionManager.companyID);

		loggingInterceptor(
			companyId,
			session.PrismGuid,
			session.SessionId
		);

		sessionInterceptor({
			authSchema: 'UDSLongToken',
			token: this.setInterceptorToken,
		});

		await setDatadogUserContext();
		await pendoInit();
		registerStaffWidget();
		registerReleaseNotes();
		openReleaseNotesModal();

		this.initialized.emit();
	}
}
